import { Dispatch, MouseEventHandler, ReactElement, SetStateAction } from 'react';
import { space } from '@op/opds.tokens';
import { Message, useMessageGetter } from '@messageformat/react';
import { Col, Form, Row, Tile } from '@op/opds.opux';
import ResetButton from './ResetButton';
import StepperFormRow from './FormRow';

interface TabProps {
  stockInvestment: number;
  stockInvestmentPeriod: number;
  stockAnnualReturn: number;
  stockTradingCommission: number;
  stockMinimumFee: number;
  stockPriceCeiling: number;
  stockCurrencyChangeExpense: number;
  setStockInvestment: Dispatch<SetStateAction<number>>;
  setStockInvestmentPeriod: Dispatch<SetStateAction<number>>;
  setStockAnnualReturn: Dispatch<SetStateAction<number>>;
  setStockTradingCommission: Dispatch<SetStateAction<number>>;
  setStockMinimumFee: Dispatch<SetStateAction<number>>;
  setStockPriceCeiling: Dispatch<SetStateAction<number>>;
  setStockCurrencyChangeExpense: Dispatch<SetStateAction<number>>;
  resetValues: MouseEventHandler<HTMLButtonElement>;
  resetPressed: Boolean;
}

const StocksTab = ({
  stockInvestment,
  stockInvestmentPeriod,
  stockAnnualReturn,
  stockTradingCommission,
  stockMinimumFee,
  stockPriceCeiling,
  stockCurrencyChangeExpense,
  setStockInvestment,
  setStockInvestmentPeriod,
  setStockAnnualReturn,
  setStockTradingCommission,
  setStockMinimumFee,
  setStockPriceCeiling,
  setStockCurrencyChangeExpense,
  resetValues,
  resetPressed,
}: TabProps): ReactElement => {
  const getText = useMessageGetter('course6.task1');
  return (
    <div>
      <p className="investoracademy-stepper-tab-body">
        <Message id="course6.task1.tab2.body" />
      </p>
      <Tile style={{ marginTop: space.default, width: '100%' }}>
        <Row>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={6}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title1" />
            </h4>
            <Form spacing="small" className="investoracademy-stepper-form">
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper1_label')}
                value={stockInvestment}
                min={500}
                max={10000}
                step={50}
                unit="€"
                onChange={setStockInvestment}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper2_label')}
                value={stockInvestmentPeriod}
                min={1}
                max={30}
                step={1}
                unit={getText('abbr.year')}
                onChange={setStockInvestmentPeriod}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper3_label')}
                value={stockAnnualReturn}
                min={-100}
                max={100}
                step={1}
                unit="%"
                onChange={setStockAnnualReturn}
              />
            </Form>
          </Col>
          <Col style={{ display: 'block', marginBottom: space.xs }} xs={12} md={6}>
            <h4 className="investoracademy-stepper-section-title">
              <Message id="course6.task1.stepper_section_title2" />
            </h4>
            <Form spacing="small" className="investoracademy-stepper-form">
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper4_label')}
                value={stockTradingCommission}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setStockTradingCommission}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper5_label')}
                value={stockMinimumFee}
                min={0}
                max={50}
                step={1}
                unit="€"
                onChange={setStockMinimumFee}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper6_label')}
                value={stockPriceCeiling}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setStockPriceCeiling}
              />
              <StepperFormRow
                aria-hidden="true"
                label={getText('tab2.stepper7_label')}
                value={stockCurrencyChangeExpense}
                min={0}
                max={10}
                step={0.1}
                unit="%"
                onChange={setStockCurrencyChangeExpense}
              />
            </Form>
          </Col>
        </Row>
        <ResetButton onClick={resetValues} />
        <p
          className="investoracademy-total-text investoracademy-textHighlight-small"
          style={{ textAlign: 'center', paddingTop: space.small }}
          aria-live="polite"
        >
          {resetPressed && <Message id="course6.task1.reset_text" />}
        </p>
      </Tile>
    </div>
  );
};

export default StocksTab;
